import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import {
  MiniVerticalService,
  MiniVerticalIndex,
  MiniVerticalIndexEntry,
} from '@services/http/mini-verticals.service';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { ActivatedRoute, ParamMap, RouterLink } from '@angular/router';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import {
  catchError,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { SEOService } from '@services/logic/seo.service';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { indexPagePath } from '@helpers/indexPagePath';

interface CategoryRoute {
  mainCategory: string;
  subCategory: string;
  subSubCategory: string;
}

@Component({
  selector: 'app-mini-vertical-index',
  imports: [
    FooterComponent,
    RouterLink,
    BreadcrumbListComponent,
    TranslatePipe,
  ],
  providers: [SEOService, TranslatePipe],
  templateUrl: './mini-vertical-index.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../tips-index/tips-index.component.scss',
})
export class MiniVerticalIndexComponent {
  private readonly env = inject(EnvToken);
  private readonly seoSegments = inject(SeoSegmentsToken);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly miniVerticalService = inject(MiniVerticalService);
  private readonly seoService = inject(SEOService);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly route = toSignal(
    this.activatedRoute.paramMap.pipe(map(toCategoryRoute)),
    { initialValue: toCategoryRoute(this.activatedRoute.snapshot.paramMap) },
  );

  private readonly activeCategory = computed(() => {
    const { mainCategory, subCategory, subSubCategory } = this.route();
    return subSubCategory || subCategory || mainCategory;
  });

  /** `undefined` while loading, `null` when the API had no index. */
  private readonly response = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(toCategoryRoute),
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous) === JSON.stringify(current),
      ),
      switchMap((route) => {
        const slug =
          route.subSubCategory || route.subCategory || route.mainCategory;
        if (!slug) {
          return of(null);
        }
        return this.miniVerticalService
          .fetchMiniVerticalIndexByCategorySlug(slug)
          .pipe(
            catchError(() => of(null)),
            tap((index) => this.setMeta(route, index)),
            startWith(undefined),
          );
      }),
    ),
    { initialValue: undefined },
  );

  protected readonly loading = computed(() => this.response() === undefined);
  protected readonly index = computed(() => this.response() ?? null);
  protected readonly miniVerticals = computed(
    () => this.index()?.miniVerticals ?? [],
  );
  protected readonly subCategories = computed(
    () => this.index()?.subCategories ?? [],
  );
  protected readonly mainCategoryTitle = computed(
    () => this.index()?.mainCategory?.title || this.route().mainCategory,
  );

  protected readonly activeCategoryTitle = computed(
    () =>
      this.index()?.category?.title ||
      toTitle(toReadableText(this.activeCategory())),
  );

  protected readonly mainIndexPath = computed(() =>
    indexPagePath(this.seoSegments.indexPage.miniVerticals, [
      this.index()?.mainCategory?.slug || this.route().mainCategory,
    ]),
  );

  protected readonly partsOfUrl = computed(() => {
    const index = this.index();
    const slugs: string[] = [];
    const hubs = this.hubChain(index).map((category) => {
      slugs.push(category.slug);
      return {
        name: category.title,
        url: `${this.env.domain}/${slugs.join('/')}/${this.seoSegments.suffix.hubIndex}`,
      };
    });
    return [
      ...hubs,
      {
        name: this.translatePipe.transform(
          'SEO.SCHEMA.MINI_SERVICES_INDEX_NAME',
        ),
        url: `${this.env.domain}${this.indexPath(this.route(), index)}`,
      },
    ];
  });

  protected generateMiniVerticalLink(
    miniVertical: MiniVerticalIndexEntry,
  ): string {
    if (miniVertical.path) {
      return miniVertical.path;
    }
    const mainSlug =
      this.index()?.mainCategory?.slug || this.route().mainCategory;
    return `/${mainSlug}/${miniVertical.newSlug || miniVertical.slug}/${this.seoSegments.suffix.miniService}`;
  }

  private indexPath(
    route: CategoryRoute,
    index: MiniVerticalIndex | null,
  ): string {
    return (
      index?.indexPath ??
      indexPagePath(this.seoSegments.indexPage.miniVerticals, [
        route.mainCategory,
        route.subCategory,
        route.subSubCategory,
      ])
    );
  }

  private hubChain(
    index: MiniVerticalIndex | null,
  ): { slug: string; title: string }[] {
    if (!index) {
      const { mainCategory, subCategory, subSubCategory } = this.route();
      return [mainCategory, subCategory, subSubCategory]
        .filter(Boolean)
        .map((slug) => ({ slug, title: toTitle(toReadableText(slug)) }));
    }
    return [index.mainCategory, index.parentCategory, index.category]
      .filter(
        (category, position, all): category is NonNullable<typeof category> =>
          !!category &&
          all.findIndex((other) => other?.slug === category.slug) === position,
      )
      .map((category) => ({
        slug: category.slug,
        title: category.title || toTitle(category.slug),
      }));
  }

  private setMeta(route: CategoryRoute, index: MiniVerticalIndex | null): void {
    const categoryTitle =
      index?.category?.title ||
      toTitle(
        toReadableText(
          route.subSubCategory || route.subCategory || route.mainCategory,
        ),
      );
    this.seoService.setPageMeta({
      metaTitle: `${this.translatePipe.transform('PAGES.MINI_SERVICES_INDEX.H1_PREFIX')} ${categoryTitle} | ${this.env.appName}`,
      metaDesc: this.translatePipe
        .transform('PAGES.MINI_SERVICES_INDEX.META_DESCRIPTION')
        .replace('{{category}}', categoryTitle.toLowerCase()),
      canonicalUrl: `${this.env.domain}${this.indexPath(route, index)}`,
    });
  }
}

function toCategoryRoute(params: ParamMap): CategoryRoute {
  return {
    mainCategory: params.get('headCategory') ?? '',
    subCategory: params.get('subCategory') ?? '',
    subSubCategory: params.get('subSubCategory') ?? '',
  };
}

function toReadableText(category: string): string {
  return category
    .replace(/-/g, ' ')
    .replace(/oe/g, 'ø')
    .replace(/aa/g, 'å')
    .replace(/ae/g, 'æ');
}

function toTitle(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}
